import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';
import templateOnly from '@ember/component/template-only';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { ExternalLink as ExternalLink$1 } from 'ember-primitives';
import { htmlSafe } from '@ember/template';

const colorsFor = variant => {
  if (variant === 'default' || variant === undefined) {
    return 'text-white bg-[var(--code-bg)] hover:bg-[var(--code-highlight-bg)]';
  }
  if (variant === 'primary') {
    return 'text-black font-bold bg-[var(--horizon-blue)]';
  }
  if (variant === 'secondary') {
    return 'text-black font-bold bg-[var(--horizon-yellow)]';
  }
};
const Button = setComponentTemplate(precompileTemplate("\n  <button type=\"button\" class=\"{{colorsFor @variant}}\n      inline-block grid grid-flow-col items-center gap-2 rounded border border-[var(--horizon-border)] px-3 py-2 shadow hover:shadow-sm focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring disabled:opacity-30\" ...attributes>\n    {{yield}}\n  </button>\n", {
  strictMode: true,
  scope: () => ({
    colorsFor
  })
}), templateOnly());
const FAB = setComponentTemplate(precompileTemplate("\n  <button type=\"button\" class=\"ring-ember-brand grid aspect-square grid-flow-col items-center gap-2 rounded-full border-2 bg-white p-2 text-4xl text-black drop-shadow-2xl hover:bg-[var(--ember-faint-gray)] hover:drop-shadow-xl focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring disabled:opacity-30\" ...attributes>\n    {{yield}}\n  </button>\n", {
  strictMode: true
}), templateOnly());
const FlatButton = setComponentTemplate(precompileTemplate("\n  <button type=\"button\" class=\"border-1 ring-ember-brand grid aspect-square grid-flow-col items-center gap-2 rounded-full bg-white p-2 text-2xl text-black hover:bg-[var(--ember-faint-gray)] focus:outline-none focus:ring focus-visible:outline-none focus-visible:ring disabled:opacity-30\" ...attributes>\n    {{yield}}\n  </button>\n", {
  strictMode: true
}), templateOnly());

const DefaultContent = setComponentTemplate(precompileTemplate("\n  <span>{{yield}}</span>\n\n  <FaIcon @icon={{faExternalLinkAlt}} />\n", {
  strictMode: true,
  scope: () => ({
    FaIcon,
    faExternalLinkAlt
  })
}), templateOnly());
const ExternalLink = setComponentTemplate(precompileTemplate("\n  <PrimitiveExternalLink class=\"inline-flex items-baseline gap-2 rounded-sm hover:underline focus:outline-none focus:ring-4 focus-visible:outline-none\" ...attributes>\n    {{#if (has-block)}}\n      <DefaultContent>\n        {{yield}}\n      </DefaultContent>\n    {{else if (has-block \"custom\")}}\n      {{yield DefaultContent to=\"custom\"}}\n    {{else if @iconOnly}}\n      <FaIcon @icon={{faExternalLinkAlt}} />\n    {{/if}}\n  </PrimitiveExternalLink>\n", {
  strictMode: true,
  scope: () => ({
    PrimitiveExternalLink: ExternalLink$1,
    DefaultContent,
    FaIcon,
    faExternalLinkAlt
  })
}), templateOnly());

const classList = htmlSafe(`
  inline-block items-center grid-flow-col
  text-white rounded bg-[var(--code-bg)]
  px-3 py-2
  border border-[var(--horizon-border)]
  hover:underline
  focus:outline-none focus:ring
  focus-visible:outline-none focus-visible:ring
  shadow
  grid gap-2
`);
const Link = setComponentTemplate(precompileTemplate("\n  {{#if @isDisabled}}\n\n    <span class=\"{{classList}} opacity-30\" ...attributes tabindex=\"0\">\n      {{yield}}\n    </span>\n\n  {{else}}\n\n    {{!-- href attribute should be passed in to Link --}}\n    {{!-- The lints maybe need a runtime integration. --}}\n    {{!-- template-lint-disable link-href-attributes --}}\n    <a class={{classList}} ...attributes>\n      {{yield}}\n    </a>\n\n  {{/if}}\n", {
  strictMode: true,
  scope: () => ({
    classList
  })
}), templateOnly());

/* eslint-disable @typescript-eslint/no-non-null-assertion */

/* eslint-disable @typescript-eslint/no-misused-promises */

let HIGHLIGHT;
let promise;
async function getHighlighter() {
  if (promise) {
    await promise;
  }
  if (HIGHLIGHT) return HIGHLIGHT;
  const {
    createHighlighterCore,
    createOnigurumaEngine,
    wasm,
    themeDark: dark,
    themeOneDarkPro: oneDarkPro,
    markdown,
    javascript,
    css,
    html,
    handlebars,
    glimmerJS,
    jsonc,
    svelte,
    vue,
    jsx,
    mermaid,
    bash
  } = await import('./highlighter-parts-I3AuQeBy.js');
  promise = createHighlighterCore({
    themes: [{
      ...dark.default,
      colors: {
        ...dark.default.colors,
        'editor.background': 'var(--code-bg)'
      }
    }, {
      ...oneDarkPro.default,
      colors: {
        ...oneDarkPro.default.colors,
        'editor.background': 'var(--code-bg)'
      }
    }],
    langs: [javascript, css, html, handlebars, glimmerJS, jsonc, svelte, vue, jsx, mermaid, bash, {
      // This *does* have embeddedLanguagesLazy
      // Just not embeddedLanguages

      ...markdown.default[0],
      embeddedLangs: ['javascript', 'css', 'html', 'glimmer-js',
      // 'glimmer-ts',
      // 'typescript',
      'handlebars', 'jsonc', 'svelte', 'vue', 'jsx', 'mermaid']
    }],
    langAlias: {
      gjs: 'glimmer-js',
      gts: 'glimmer-ts',
      glimdown: 'markdown',
      gmd: 'markdown',
      gdm: 'markdown',
      json: 'jsonc'
    },
    engine: createOnigurumaEngine(() => wasm)
  });
  const highlighter = await promise;
  HIGHLIGHT = highlighter;
  return highlighter;
}
async function createShiki() {
  const {
    createHighlighterCore,
    createOnigurumaEngine,
    wasm,
    themeDark,
    themeOneDarkPro,
    javascript,
    typescript,
    bash,
    css,
    html,
    glimmerJS,
    glimmerTS,
    handlebars,
    jsonc,
    svelte,
    vue,
    jsx,
    mermaid
  } = await import('./highlighter-parts-I3AuQeBy.js');
  const highlighter = await createHighlighterCore({
    themes: [themeDark, themeOneDarkPro],
    langs: [javascript, typescript, bash, css, html, glimmerJS, glimmerTS, handlebars, jsonc, svelte, vue, jsx, mermaid],
    engine: createOnigurumaEngine(() => wasm)
  });
  return highlighter;
}

export { Button, ExternalLink, FAB, FlatButton, Link, createShiki, getHighlighter };
//# sourceMappingURL=index.js.map
